import { CategoryKey, FIN_CATEGORY_BY_KEY, isTechnical } from '#dictionary'
import { formatCategoryName } from '#formatting'

import createTimelineRecord from '../timeline/createTimelineRecord'
import type { TimelineRecord } from '../timeline/TimelineRecord'
import type { AnnualFinalValues } from './TimelineGenerator'

// TODO(ZL-961): duplicates the `annualFinalValues` map from `TimelineGenerator`
// because this class doesn't extend it. Idempotent today only because `savings`
// is recomputed from the incoming `timeline` on every call — an accidental
// += anywhere in this class would silently reintroduce the ZL-957 bug shape.
class SavingsGenerator {
  private readonly annualFinalValues: AnnualFinalValues = new Map()
  private timelineRecords: TimelineRecord[] = []

  generate(year: number, timeline: TimelineRecord[]): TimelineRecord[] {
    // Store timeline records for finding original records
    this.timelineRecords = timeline

    const doubleEntryRecords = timeline.filter(record => {
      const category = FIN_CATEGORY_BY_KEY.get(record.categoryKey)
      return category !== undefined && isTechnical(category)
    })

    const previousYearValue = this.previousYearFinalValue(year)
    const savings = doubleEntryRecords.reduce(
      (acc, record) => acc + record.value,
      previousYearValue
    )

    this.setYearFinalValue(year, savings)

    const description = this.buildSavingsDescription(
      year,
      doubleEntryRecords,
      previousYearValue,
      savings
    )

    return [
      createTimelineRecord({
        year,
        value: savings,
        description,
        categoryKey: CategoryKey.catAssetsCash,
        intent: 'Savings',
      }),
    ]
  }

  private previousYearFinalValue(year: number): number {
    return this.annualFinalValues.get(year - 1) || 0
  }

  private setYearFinalValue(year: number, value: number): void {
    this.annualFinalValues.set(year, value)
  }

  private buildSavingsDescription(
    year: number,
    doubleEntryRecords: TimelineRecord[],
    previousYearValue: number,
    totalSavings: number
  ): string {
    const contributingRecordsTable = this.buildContributingRecordsSection(
      doubleEntryRecords,
      previousYearValue
    )

    return this.buildSavingsSummary(
      year,
      previousYearValue,
      contributingRecordsTable,
      totalSavings
    )
  }

  private buildSavingsSummary(
    year: number,
    _previousYearValue: number,
    contributingRecordsTable: string,
    _totalSavings: number
  ): string {
    const title = this.buildTitle(year)
    const contributingRecordsSection = this.buildContributingRecordsHeader(
      contributingRecordsTable
    )

    return `${title}

${contributingRecordsSection}`
  }

  private buildTitle(year: number): string {
    return `## Savings Summary (${year})`
  }

  private buildContributingRecordsHeader(
    contributingRecordsTable: string
  ): string {
    return `### Contributing Records

${contributingRecordsTable}`
  }

  private buildContributingRecordsSection(
    doubleEntryRecords: TimelineRecord[],
    previousYearValue: number
  ): string {
    if (doubleEntryRecords.length > 0) {
      const tableRows = this.buildTableRows(
        doubleEntryRecords,
        previousYearValue
      )
      return this.buildTable(tableRows, previousYearValue)
    } else {
      return `**No double-entry records this year**`
    }
  }

  private buildTableRows(
    doubleEntryRecords: TimelineRecord[],
    previousYearValue: number
  ): string[] {
    let runningTotal = previousYearValue
    return doubleEntryRecords.map((record, index) => {
      runningTotal += record.value
      return this.buildTableRow(index + 1, record, record.value, runningTotal)
    })
  }

  private buildTableRow(
    rowNumber: number,
    record: TimelineRecord,
    diffValue: number,
    finalValue: number
  ): string {
    const coloredDiff = this.formatDiffValue(diffValue)
    const recordDescription = this.buildRecordDescription(record)
    return `| ${rowNumber} | ${recordDescription} | ${coloredDiff} | $${finalValue.toFixed(0)} |`
  }

  private buildRecordDescription(record: TimelineRecord): string {
    if (!record.doubleEntryFromRecordId) {
      return this.sanitizeDescription(record.description || 'Unknown Record')
    }

    const originalRecord = this.findOriginalRecord(
      record.doubleEntryFromRecordId
    )
    if (!originalRecord) {
      return this.sanitizeDescription(record.description || 'Unknown Record')
    }

    const firstEntryCategory = FIN_CATEGORY_BY_KEY.get(
      originalRecord.categoryKey
    )
    if (!firstEntryCategory) {
      return this.sanitizeDescription(record.description || 'Unknown Record')
    }

    // Build the description: "Category Name + Record Name"
    const categoryName = formatCategoryName(firstEntryCategory, {
      with: 'group',
    })
    const recordName = originalRecord.description || 'Unknown Record'
    const firstLine = recordName.split('\n')[0].trim()

    return `${categoryName} + ${firstLine}`
  }

  private findOriginalRecord(recordId: string): TimelineRecord | null {
    return this.timelineRecords.find(record => record.id === recordId) || null
  }

  private formatDiffValue(diffValue: number): string {
    const sign = diffValue >= 0 ? '+' : ''
    const value = `$${diffValue.toFixed(0)}`

    if (diffValue > 0) {
      return `${sign}${value} 🟢`
    } else if (diffValue < 0) {
      return `${sign}${value} 🔴`
    } else {
      return `${value} ⚪`
    }
  }

  private buildTable(rows: string[], previousYearValue: number): string {
    const headers = `| # | Record Description | Diff | Final Value |
|--|-------------------|------|-------------|`

    // Add carryover balance as first row
    const carryoverRow = `| 0 | **Carryover from Previous Year** | $0 ⚪ | $${previousYearValue.toFixed(0)} |`

    // Calculate final balance
    const finalBalance =
      rows.length > 0
        ? rows[rows.length - 1].split('|')[4].trim()
        : `$${previousYearValue.toFixed(0)}`

    // Add final balance as last row
    const finalRow = `| **Total** | **Final Balance** | **${this.calculateTotalDiff(rows)}** | **${finalBalance}** |`

    return `${headers}\n${carryoverRow}\n${rows.join('\n')}\n${finalRow}`
  }

  private calculateTotalDiff(rows: string[]): string {
    let totalDiff = 0
    rows.forEach(row => {
      const diffCell = row.split('|')[3].trim()
      const diffValue = parseFloat(diffCell.replace(/[^-\d.]/g, ''))
      if (!isNaN(diffValue)) {
        totalDiff += diffValue
      }
    })

    const sign = totalDiff >= 0 ? '+' : ''
    const value = `$${totalDiff.toFixed(0)}`

    if (totalDiff > 0) {
      return `${sign}${value} 🟢`
    } else if (totalDiff < 0) {
      return `${sign}${value} 🔴`
    } else {
      return `${value} ⚪`
    }
  }

  private sanitizeDescription(description: string): string {
    return description.replace(/\n/g, ' ').trim()
  }
}

export default SavingsGenerator
